import { useState } from "react";
import type { SeasonArchive } from "@averagejoesff/shared";
import { clearResults } from "../../api/adminApi";
import { AdminFormStatus } from "../../components/AdminFormStatus";
import { useAdminForm } from "../../hooks/useAdminForm";

interface ClearSeasonResultsProps {
  season: number;
  idToken: string;
  /** Called with the freshly-cleared archive so the caller can update state directly. */
  onCleared: (archive: SeasonArchive) => void;
}

/**
 * A destructive, hard-to-undo action, so it's a two-step confirm rather
 * than a single button — deliberately not a native `window.confirm`, to
 * keep this in the same custom-UI style as the rest of the admin panel.
 */
export function ClearSeasonResults({ season, idToken, onCleared }: ClearSeasonResultsProps) {
  const [confirming, setConfirming] = useState(false);
  const { status, error, submit } = useAdminForm(
    () => clearResults(season, idToken),
    (archive: SeasonArchive) => {
      // Only on success does the confirm panel collapse — on error it
      // stays open so the message above renders next to the buttons.
      setConfirming(false);
      onCleared(archive);
    },
  );

  function handleConfirm() {
    void submit();
  }

  if (!confirming) {
    // Rendered inside a form's `.admin-panel__actions` row (see AdminPanel),
    // where CSS pushes it to the far right of the row.
    return (
      <button
        type="button"
        className="admin-panel__danger-trigger"
        onClick={() => setConfirming(true)}
      >
        Clear {season} Results
      </button>
    );
  }

  return (
    <div className="admin-panel__danger-zone">
      <p className="admin-panel__danger-warning">
        This permanently deletes every entered week's result for {season} — teams are not
        affected. This can't be undone.
      </p>
      <div className="admin-panel__actions">
        <button type="button" onClick={() => setConfirming(false)} disabled={status === "pending"}>
          Cancel
        </button>
        <button
          type="button"
          className="admin-panel__danger-confirm"
          onClick={handleConfirm}
          disabled={status === "pending"}
        >
          {status === "pending" ? "Clearing…" : "Yes, clear all results"}
        </button>
        <AdminFormStatus status={status} error={error} />
      </div>
    </div>
  );
}
